import * as React from 'react'
// libs
import { useTheme } from '@mui/material'
// components
import InputLabel from '../components/InputLabel'
import FormControl from '@mui/material/FormControl'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
// types
import { Theme } from '@mui/material'
import { GroupBase } from 'react-select'
import { AsyncProps } from 'react-select/async'
import GooglePlacesAutocompleteProps from 'react-google-places-autocomplete/build/types'
// views
import { Wrapper } from './views'
// utils
import { extractAddress } from 'components/FormElements/LocationPicker/utils'
import { getDefaultSelectStyles, reactSelectTheme } from 'components/FormElements/Select/views'

export type Place = {
  city: string
  state: string
  country: string
  address: string
  latitude: number | null
  longitude: number | null
}

export type PlaceOption = {
  label: string
  value: {
    description: string
    place_id: string
  }
}

export interface LocationPickerProps
  extends
    GooglePlacesAutocompleteProps,
    Omit<AsyncProps<PlaceOption, false, GroupBase<PlaceOption>>, 'theme' | 'value'> {
  value?: { value: Place }
  label?: string
  isError?: boolean
  handleBlur?: () => void
  handleFocus?: () => void
  handleChange?: (...args: any) => void
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  name,
  label,
  value,
  isError,
  required,
  isDisabled,
  handleBlur,
  handleFocus,
  handleChange,
  defaultValue,
  ...props
}) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const muiTheme = useTheme<Theme>()

  const isLabelExist = !!label
  const isLabelShrunk = isLabelExist && (isFocused || !!value.value)

  const onBlur = () => {
    isFocused && setIsFocused(false)
    handleBlur?.()
  }

  const onFocus = () => {
    !isFocused && setIsFocused(true)
    handleFocus?.()
  }

  const onChange = async (place: PlaceOption) => {
    try {
      const geocode = await geocodeByPlaceId(place.value?.place_id)

      if (place?.value && geocode && geocode[0]) {
        const { city, state, country, coordinates } = extractAddress(geocode)

        const address = {
          city,
          state,
          country,
          ...coordinates,
          address: place.value.description
        }

        handleChange({ value: address })
      }
    } catch (error) {
      console.error('Error fetching place details:', error)
    }
  }

  return (
    <FormControl disabled={isDisabled} error={isError} fullWidth>
      {isLabelExist && (
        <InputLabel focused={isFocused} shrink={isLabelShrunk} required={required}>
          {label}
        </InputLabel>
      )}
      <Wrapper className='location-select-wrapper'>
        <GooglePlacesAutocomplete
          debounce={200}
          minLengthAutocomplete={2}
          apiKey={import.meta.env.VITE_GEOSUGGEST_KEY}
          selectProps={{
            ...props,
            classNamePrefix: 'react-select',
            onBlur,
            onFocus,
            onChange,
            value: value?.value ? { label: value.value.address, value: value.value } : null,
            theme: reactSelectTheme,
            menuPortalTarget: document.querySelector('body'),
            styles: getDefaultSelectStyles({
              isError,
              isFocused,
              isDisabled,
              theme: muiTheme,
              hasInputValue: !!value.value,
              overrides: {
                singleValue: (styles) => ({
                  ...styles,
                  color: isDisabled ? '#999999' : 'black',
                  cursor: isDisabled ? 'default' : 'pointer'
                }),
                valueContainer: (styles) => ({
                  ...styles,
                  padding: 0,
                  fontSize: 16,
                  gap: 4,
                  height: 32
                }),
                input: (styles) => ({
                  ...styles,
                  overflow: 'hidden',
                  position: value.value ? 'initial' : 'absolute',
                  display: isDisabled ? 'none' : 'block',
                  zIndex: 1,
                  height: 32
                })
              }
            })
          }}
        />
      </Wrapper>
    </FormControl>
  )
}

export default LocationPicker
